// InventoryView — CONTAINERIZED: every conceptual container is its own card
// (bags pane, toolbag space, Worn paper-doll, Broken pile, Ground); grids of
// slots sit in sunken recesses. LEFT = the BAGS pane, Ground (always visible),
// Broken; RIGHT = Worn (ghost glyphs, no labels) + Toolbag.
// BAGS pane = ONE row: icon-only TABS left — one per bagspace: equipped bags
// show their icon, empty spaces a muted "+" tab, plus one muted locked tab
// (selection is client VIEW state in sessionStorage) — and, RIGHT, only the
// SELECTED tab's equip slot (swap slot / empty space / locked), with the
// selected bag's grid below (empty/locked: a faint hint line instead).
// A bagspace tab is also a DROP TARGET: dropping an item on it sends
// drag_drop into that bag's first free slot — or into the empty space's
// equip slot, so equipping a new bag stays possible (full bag: the drop
// just switches to it) — same event, both DnD paths (slots.bagTabDropDest).
// Ground pads to full rows + spare blank rows (a live indexed drop pile).

import { clear, el, kbtn, armBtn, tooltip } from '../kit';
import * as icons from '../icons';
import * as vsj from '../vsj';
import { sendEvent } from '../net';
import { slotNode, bindGrid, bagTabDropDest } from '../components/slots';
import { statChip } from '../statvocab';

const GEAR_LAYOUT = [['head'], ['main_hand', 'body', 'off_hand'], ['legs']];
const GROUND_SPARE_ROWS = 2;
const GROUND_COLS = 8;

export function inventoryView(): { root: HTMLElement; refresh(changed: Set<string>): void } {
  const bagsBox = el('div', { style: 'display:flex;flex-direction:column;gap:10px' });
  const groundBox = el('div');
  const brokenBox = el('div');
  const gearBox = el('div');
  const toolbagBox = el('div');

  const left = el(
    'div',
    { class: 'k-scroll', style: 'flex:1;min-width:0;display:flex;flex-direction:column;gap:10px' },
    bagsBox, groundBox, brokenBox,
  );
  const right = el(
    'div',
    { class: 'k-scroll', style: 'flex:1;min-width:0;display:flex;flex-direction:column;gap:10px' },
    gearBox, toolbagBox,
  );
  const root = el(
    'div',
    { class: 'k-panel', style: 'flex:1;min-height:0;display:flex;gap:10px;padding:10px' },
    left, right,
  );

  const containerCard = (quiet = false): { panel: HTMLElement; col: HTMLElement } => {
    const col = el('div', { style: 'display:flex;flex-direction:column;gap:6px' });
    const panel = el('div', {
      class: 'k-panel' + (quiet ? ' quiet' : ' raised'),
      style: 'padding:8px',
    });
    panel.append(col);
    return { panel, col };
  };

  const recess = (content: HTMLElement): HTMLElement =>
    el('div', {
      class: 'k-panel quiet',
      style: 'background:var(--bg);padding:6px',
    }, content);

  const spaceHeader = (
    spaceInv: string, key: string, locked: boolean, bagItem: string | null,
    title: string, hint: string,
  ): HTMLElement => {
    const slotData = bagItem
      ? { item: bagItem, count: 1, name: vsj.infoName(bagItem) }
      : null;
    return el(
      'div',
      { style: 'display:flex;gap:8px;align-items:center' },
      slotNode(spaceInv, key, slotData, { locked, space: !locked }),
      el(
        'div',
        { style: 'flex:1;min-width:0' },
        el('div', { text: title }),
        el('div', { class: 'k-small k-faint', text: hint }),
      ),
    );
  };

  // ---- bags: tabbed pane ----
  // Selected tab = client VIEW state only (which served bag to show).
  const BAG_TAB_KEY = 'inventory.bagTab';
  const storedTab = (): number => {
    try { return Number(sessionStorage.getItem(BAG_TAB_KEY) ?? -1); } catch { return -1; }
  };
  const selectTab = (i: number) => {
    try { sessionStorage.setItem(BAG_TAB_KEY, String(i)); } catch { /* view state only */ }
    refreshBags();
  };

  // icon-only tab per bagspace (equipped bag icon, or a muted "+" when the
  // space is empty); also a drop target — routed by slots.bagTabDropDest
  // (bag's first free slot / empty space's equip slot / full bag: switch)
  const bagTab = (i: number, bagItem: string | null, selected: boolean): HTMLElement => {
    const tab = el(
      'button',
      {
        class: 'k-btn bag-tab' + (selected ? ' active' : '') + (bagItem ? '' : ' vacant'),
        dataset: { bagtab: String(i) },
      },
      bagItem
        ? icons.texture(bagItem, 24)
        : el('span', { class: 'k-faint', text: '+' }),
    );
    if (bagItem) {
      const info = vsj.info(bagItem) ?? {};
      const name = vsj.infoName(bagItem);
      tooltip(tab, () => (info.bag_slots != null ? `${name} — ${info.bag_slots} slots` : name));
    } else {
      tooltip(tab, () => 'Empty bag space — drop a bag here to carry more.');
    }
    tab.addEventListener('click', () => selectTab(i));
    tab.addEventListener('dragover', (e) => {
      if (e.dataTransfer?.types.includes('application/x-stoneheim-slot')) {
        e.preventDefault();
        tab.classList.add('drop-hover');
      }
    });
    tab.addEventListener('dragleave', () => tab.classList.remove('drop-hover'));
    tab.addEventListener('drop', (e) => {
      e.preventDefault();
      tab.classList.remove('drop-hover');
      const raw = e.dataTransfer?.getData('application/x-stoneheim-slot');
      if (!raw) return;
      try {
        const src = JSON.parse(raw);
        const dest = bagTabDropDest(i);
        if (dest == null) { selectTab(i); return; } // full bag — just show it
        sendEvent('drag_drop', { src, dest });
      } catch {
        /* malformed drag payload — ignore */
      }
    });
    return tab;
  };

  const refreshBags = () => {
    clear(bagsBox);
    const bags = vsj.at('bags') ?? {};
    const ch = vsj.at('character') ?? {};
    let total = Number(ch.bag_spaces ?? Object.keys(bags).length);
    for (const k of Object.keys(bags)) total = Math.max(total, Number(k) + 1);
    const bagAt = (i: number): string | null =>
      bags[String(i)]?.bag != null ? String(bags[String(i)].bag) : null;
    const occupied: number[] = [];
    for (let i = 0; i < total; i++) if (bagAt(i) != null) occupied.push(i);
    // tab indices 0..total-1 = bagspaces; index <total> = the locked space
    const stored = storedTab();
    const sel = stored >= 0 && stored <= total ? stored : occupied[0] ?? 0;

    // served unlock level when the driver knows it, vague fallback otherwise
    const lockedHint = ch.next_bag_space_at != null
      ? `Unlocks at character level ${ch.next_bag_space_at}`
      : 'More spaces unlock as you level.';

    const card = containerCard();
    // ONE row — a tab per bagspace (+ the muted locked tab) LEFT, and ONLY
    // the SELECTED tab's equip slot RIGHT
    const tabs = el('div', { class: 'bag-tabs', style: 'flex:1;min-width:0' });
    for (let i = 0; i < total; i++) tabs.append(bagTab(i, bagAt(i), i === sel));
    const lockedTab = el(
      'button',
      { class: 'k-btn bag-tab vacant' + (sel === total ? ' active' : '') },
      el('span', { class: 'k-faint', text: '🔒' }),
    );
    tooltip(lockedTab, () => lockedHint);
    lockedTab.addEventListener('click', () => selectTab(total));
    tabs.append(lockedTab);

    const selBag = sel < total ? bagAt(sel) : null;
    const space = el('div', { style: 'display:flex;gap:4px;flex:none' });
    if (sel === total) {
      const lockedSlot = slotNode('bagspace', String(total), null, { locked: true });
      tooltip(lockedSlot, () => lockedHint);
      space.append(lockedSlot);
    } else {
      space.append(
        slotNode(
          'bagspace', String(sel),
          selBag ? { item: selBag, count: 1, name: vsj.infoName(selBag) } : null,
          { space: true, tip: selBag ? undefined : 'Drop a bag here to carry more.' },
        ),
      );
    }
    card.col.append(el('div', { style: 'display:flex;gap:10px;align-items:center' }, tabs, space));

    if (selBag != null) {
      const grid = el('div');
      bindGrid(grid, `bag${sel}`, bags[String(sel)].slots ?? {});
      card.col.append(recess(grid));
    } else {
      card.col.append(
        el('div', {
          class: 'k-small k-faint',
          text: sel === total ? lockedHint : 'Empty bag space — drop a bag on the slot to carry more.',
        }),
      );
    }
    bagsBox.append(card.panel);
  };

  // ---- toolbag: PAIRED slots (active + reserves sub-slot per index) ----
  const reserveNode = (key: string, active: any, spare: any): HTMLElement => {
    if (!active) {
      // fixed footprint: an invisible placeholder, never a drop target
      return el('div', { class: 'tb-reserve-void' });
    }
    const name = String(active.name ?? vsj.infoName(String(active.item)));
    const tip = spare
      ? `Reserves — spare ${name}s. One moves up when the active breaks.`
      : `Reserves — stash spare ${name}s here.`;
    const node = slotNode('toolbagres', key, spare, { tip });
    node.classList.add('tb-reserve');
    return node;
  };

  const refreshToolbag = () => {
    clear(toolbagBox);
    const tb = vsj.at('toolbag') ?? {};
    const bagItem = tb.bag != null ? String(tb.bag) : null;
    const card = containerCard();
    card.col.append(
      spaceHeader(
        'toolbagspace', '0', false, bagItem,
        bagItem ? vsj.infoName(bagItem) : 'Toolbag (built-in slot)',
        bagItem ? 'Drag the toolbag out to swap it.' : 'Drop a toolbag here.',
      ),
    );
    const slots = tb.slots ?? {};
    const res = tb.res ?? {};
    const row = el('div', { class: 'tb-pairs' });
    const keys = Object.keys(slots).sort((a, b) => Number(a) - Number(b));
    for (const k of keys) {
      row.append(
        el('div', { class: 'tb-pair' }, slotNode('toolbag', k, slots[k]), reserveNode(k, slots[k], res[k])),
      );
    }
    card.col.append(recess(row));
    // served one-line hint (spare-tool auto-promote behavior)
    if (tb.spare_note != null)
      card.col.append(el('div', { class: 'k-small k-faint', text: String(tb.spare_note) }));
    toolbagBox.append(card.panel);
  };

  // ---- gear paper-doll ----
  // Readout under the doll: the served `equipment_deltas` rows ({stat, value}
  // — pre-signed server-authored strings, display order) via statvocab chips;
  // old servers without the slice fall back to the derived_stats.combat
  // with_eq columns (server truth either way, presentation only here).
  const gearStatsRow = (): HTMLElement | null => {
    const row = el('div', { class: 'stat-row', style: 'justify-content:center' });
    const deltas = vsj.at('equipment_deltas');
    if (Array.isArray(deltas)) {
      for (const d of deltas) {
        if (!d || typeof d !== 'object' || d.stat == null) continue;
        row.append(statChip(String(d.stat), String(d.value ?? '')));
      }
      return row.childElementCount ? row : null;
    }
    const combat = vsj.at('derived_stats.combat') ?? {};
    for (const key of Object.keys(combat)) {
      const r = combat[key];
      if (!r || typeof r !== 'object' || r.stat == null) continue;
      row.append(statChip(String(r.stat), Math.round(Number(r.with_eq ?? 0))));
    }
    return row.childElementCount ? row : null;
  };

  // served damage rows (top-level equipment_damage: gross per-hand swing
  // damage, server-computed via the real combat formula; `math` is served
  // working riding the chip tooltip — presentation only, never derived here)
  const dmgRows = (card: { col: HTMLElement }) => {
    const rows = vsj.at('equipment_damage');
    if (!Array.isArray(rows)) return;
    for (const r of rows) {
      if (!r || typeof r !== 'object' || r.value == null) continue;
      const line = el('div', { class: 'stat-row', style: 'justify-content:center;align-items:center' });
      line.append(statChip('dmg', String(r.value), r.math != null ? String(r.math) : ''));
      if (r.weapon != null)
        line.append(el('span', { class: 'k-small k-faint', text: String(r.weapon) }));
      card.col.append(line);
    }
  };

  // served set line (top-level equipment_set) — highlighted when active
  const setLine = (card: { col: HTMLElement }) => {
    const set = vsj.at('equipment_set');
    if (!set || typeof set !== 'object') return;
    const active = !!set.active;
    const line = el(
      'div',
      { style: 'display:flex;gap:6px;align-items:center;justify-content:center;flex-wrap:wrap' },
      el('span', {
        class: 'k-small',
        style: active ? 'color:var(--gold)' : 'color:var(--text-dim)',
        text: String(set.name ?? ''),
      }),
    );
    if (set.pieces_worn != null)
      line.append(el('span', {
        class: 'k-chip',
        style: active ? 'border-color:var(--gold);color:var(--gold)' : 'color:var(--text-dim)',
        text: String(set.pieces_worn),
      }));
    card.col.append(line);
    if (set.bonus_text != null)
      card.col.append(el('div', { class: 'k-small k-faint', style: 'text-align:center', text: String(set.bonus_text) }));
  };

  const refreshGear = () => {
    clear(gearBox);
    const eq = vsj.at('equipment') ?? {};
    const card = containerCard();
    card.col.append(el('div', { class: 'k-small k-faint', text: 'Worn' }));
    for (const line of GEAR_LAYOUT) {
      const row = el('div', { style: 'display:flex;gap:4px;justify-content:center' });
      for (const slotKey of line) row.append(slotNode('gear', slotKey, eq[slotKey], { ghost: slotKey }));
      card.col.append(row);
    }
    setLine(card);
    const stats = gearStatsRow();
    if (stats) card.col.append(stats);
    dmgRows(card);
    gearBox.append(card.panel);
  };

  // ---- broken ----
  const refreshBroken = () => {
    clear(brokenBox);
    const broken = vsj.at('broken') ?? {};
    const keys = Object.keys(broken).sort((a, b) => Number(a) - Number(b));
    if (!keys.length) return;
    const card = containerCard();
    card.col.append(el('div', { class: 'k-small k-faint', text: 'Broken' }));
    for (const k of keys) {
      const b = broken[k];
      if (!b || typeof b !== 'object') continue;
      const cost = b.repair_cost;
      const costTxt =
        cost && typeof cost === 'object' ? ` (${cost.count ?? '?'}× ${vsj.infoName(String(cost.item ?? ''))})` : '';
      card.col.append(
        el(
          'div',
          { style: 'display:flex;gap:6px;align-items:center' },
          icons.texture(String(b.item ?? ''), 24),
          el('span', { class: 'k-dim', style: 'flex:1', text: vsj.infoName(String(b.item ?? '')) }),
          kbtn(b.repair, {
            label: 'Repair' + costTxt, compact: true,
            // served verdict: btn null + repair_blocked prose (anvil/materials)
            denyReason: b.repair_blocked != null ? String(b.repair_blocked) : undefined,
          }),
          armBtn(b.discard, 'Discard', { compact: true }),
        ),
      );
    }
    brokenBox.append(card.panel);
  };

  // ---- ground ----
  const paddedGround = (slots: Record<string, any>): Record<string, any> => {
    let last = -1;
    for (const k of Object.keys(slots)) if (/^\d+$/.test(k) && slots[k] != null) last = Math.max(last, Number(k));
    const shown = (Math.floor(last / GROUND_COLS) + 1 + GROUND_SPARE_ROWS) * GROUND_COLS;
    const padded: Record<string, any> = {};
    for (let i = 0; i < shown; i++) padded[String(i)] = slots[String(i)] ?? null;
    return padded;
  };

  const refreshGround = () => {
    clear(groundBox);
    const g = vsj.at('ground') ?? {};
    const card = containerCard();
    card.col.append(el('div', { class: 'k-small k-faint', text: 'Ground' }));
    const stations = g.stations ?? {};
    if (stations && typeof stations === 'object' && Object.keys(stations).length) {
      const srow = el('div', { style: 'display:flex;gap:6px;flex-wrap:wrap' });
      for (const st of Object.keys(stations)) {
        const tier = stations[st]?.tier ?? '?';
        srow.append(el('span', { class: 'k-chip', text: `${st[0].toUpperCase()}${st.slice(1)} T${tier}` }));
      }
      card.col.append(srow);
    }
    const grid = el('div');
    bindGrid(grid, 'ground', paddedGround(g.slots ?? {}));
    card.col.append(recess(grid));
    groundBox.append(card.panel);
  };

  const refreshAll = () => {
    refreshBags();
    refreshToolbag();
    refreshGear();
    refreshBroken();
    refreshGround();
  };
  refreshAll();

  return {
    root,
    refresh(changed) {
      const all = changed.has('*');
      const infos = changed.has('infos');
      if (all || infos || changed.has('bags') || changed.has('character')) refreshBags();
      if (all || infos || changed.has('toolbag')) refreshToolbag();
      if (all || changed.has('equipment') || changed.has('derived_stats') ||
          changed.has('equipment_set') || changed.has('equipment_deltas') ||
          changed.has('equipment_damage')) refreshGear();
      if (all || infos || changed.has('broken')) refreshBroken();
      if (all || infos || changed.has('ground')) refreshGround();
    },
  };
}
